import React from 'react';
import PropTypes from 'prop-types';
import { values, isEmpty, isEqual } from 'lodash';

import { Bert } from 'meteor/themeteorchef:bert';
import { Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import handleErrorInClient from '../../../modules/handle-error-in-client';
import methodPromise from '../../../modules/method-promise-wrapper';

import { ANNOTATION_EXPORT_FORMATS } from '../../../api/Annotations/Annotations';

import * as annotationOps from '../../../api/Annotations/annotation-operations';
import * as videoOps from '../../../api/Videos/video-operations';

import ColButton from '../../atoms/ColButton';
import ColSplitButton from '../../atoms/ColSplitButton';

const VideoFeatureButtons = ({
  buttonStates,
  isClickableDivHidden,
  isPublic,
  isFree,
  downloadAnnotations,
  downloadVideo,
  showIAModal,
  toggleButtonSwitch,
  changeVideoContainerStateProperty,
  video,
  ...rest
}) => {
  const { annotations, selectedAnnotation } = rest;
  const videoId = videoOps.getId(video);
  const orderAnnotations = annotations.sort(
    (first, second) => first.geoJSON.properties.videoTime - second.geoJSON.properties.videoTime
  );
  const annotationLength = !isEmpty(orderAnnotations) ? orderAnnotations.length - 1 : 0;
  const [annotation, setSelectedAnnotation] = React.useState(selectedAnnotation);
  const [count, setCount] = React.useState(0);
  const isVideoLine = videoOps.isVideoLine(video);
  const exportContentVideoLine = {
    Annotations: 'Annotations',
    Video: 'Video',
    VideoTrack: 'Video track'
  };
  const exportContentVideoPoint = {
    Annotations: 'Annotations',
    Video: 'Video'
  };
  const exportContent = isVideoLine ? exportContentVideoLine : exportContentVideoPoint;

  const handleHardRemove = async () => {
    // eslint-disable-next-line no-alert
    if (window.confirm('Are you sure? This is permanent!')) {
      try {
        await methodPromise('annotations.hardManyDelete', videoId);
        changeVideoContainerStateProperty('selectedAnnotation', {});
        Bert.alert(`Annotation deleted!`, 'danger');
      } catch (error) {
        handleErrorInClient(error);
      }
    }
  };

  const changeStatesVideoContainer = (newSelectedAnnotation, newVideoTime) => {
    changeVideoContainerStateProperty('updateVideo', true);
    changeVideoContainerStateProperty('selectedAnnotation', newSelectedAnnotation);
    changeVideoContainerStateProperty('videoTime', newVideoTime);
  };

  const handleStartAnnotation = () => {
    const newSelectedAnnotation = orderAnnotations[0];
    const newVideoTime = annotationOps.getVideoTime(newSelectedAnnotation);
    setSelectedAnnotation(newSelectedAnnotation);
    setCount(0);
    changeStatesVideoContainer(newSelectedAnnotation, newVideoTime);
  };

  const handleEndAnnotation = () => {
    const newSelectedAnnotation = orderAnnotations[annotationLength];
    const newVideoTime = annotationOps.getVideoTime(newSelectedAnnotation);
    setSelectedAnnotation(newSelectedAnnotation);
    setCount(annotationLength);
    changeStatesVideoContainer(newSelectedAnnotation, newVideoTime);
  };

  const handlePreviousAnnotation = () => {
    if (count > 0) {
      const newCount = count - 1;
      const newSelectedAnnotation = orderAnnotations[newCount];
      const newVideoTime = annotationOps.getVideoTime(newSelectedAnnotation);
      setSelectedAnnotation(newSelectedAnnotation);
      setCount(newCount);
      changeStatesVideoContainer(newSelectedAnnotation, newVideoTime);
    }
  };

  const handleNextAnnotation = () => {
    if (!isEmpty(annotation)) {
      if (count < annotationLength) {
        const newCount = count + 1;
        const newSelectedAnnotation = orderAnnotations[newCount];
        const newVideoTime = annotationOps.getVideoTime(newSelectedAnnotation);
        setSelectedAnnotation(newSelectedAnnotation);
        setCount(newCount);
        changeStatesVideoContainer(newSelectedAnnotation, newVideoTime);
      }
    } else {
      const newSelectedAnnotation = orderAnnotations[0];
      const newVideoTime = annotationOps.getVideoTime(newSelectedAnnotation);
      setSelectedAnnotation(newSelectedAnnotation);
      changeStatesVideoContainer(newSelectedAnnotation, newVideoTime);
    }
  };

  React.useEffect(() => {
    if (!isEqual(selectedAnnotation, annotation)) {
      const annotationIndex = orderAnnotations.findIndex(element =>
        isEqual(selectedAnnotation, element)
      );
      setSelectedAnnotation(annotation);
      setCount(annotationIndex);
    }
  }, [selectedAnnotation]);

  return (
    <>
      {!isPublic && (
        <>
          <ColButton
            xs={12}
            sm={3}
            size="sm"
            active={buttonStates.addAnnotationButton}
            disabled={isClickableDivHidden}
            onClick={() => toggleButtonSwitch('addAnnotationButton')}
            icon="map-marker"
            spanLabel="Point"
          />
          <ColButton
            xs={12}
            sm={3}
            size="sm"
            active={showIAModal}
            disabled={isClickableDivHidden}
            onClick={() => toggleButtonSwitch('aiAnnotationsButton')}
            icon="bullseye"
            spanLabel="Auto"
          />
        </>
      )}

      <Button
        size="sm"
        variant="secondary"
        label="Next"
        disabled={count === 0}
        onClick={handleStartAnnotation}
      >
        <FontAwesomeIcon icon="angle-double-left" />
      </Button>

      <Button
        size="sm"
        variant="secondary"
        label="Previous"
        disabled={count === 0}
        onClick={handlePreviousAnnotation}
      >
        <FontAwesomeIcon icon="angle-left" />
      </Button>

      <Button
        size="sm"
        variant="secondary"
        label="Next"
        disabled={count === annotationLength}
        onClick={handleNextAnnotation}
      >
        <FontAwesomeIcon icon="angle-right" />
      </Button>

      <Button
        size="sm"
        variant="secondary"
        label="Next"
        disabled={count === annotationLength}
        onClick={handleEndAnnotation}
      >
        <FontAwesomeIcon icon="angle-double-right" />
      </Button>

      {!isPublic && (
        <Button
          size="sm"
          variant="danger"
          disabled={isEmpty(annotations)}
          // disabled
          onClick={() => handleHardRemove()}
        >
          <FontAwesomeIcon icon="trash-alt" /> Annotations
        </Button>
      )}

      {!isFree && !isPublic && (
        <ColSplitButton
          xs={12}
          sm={3}
          variant="light"
          id="download-button"
          size="sm"
          disabled={isClickableDivHidden}
          alignRight
          icon="download"
          spanLabel="Download"
          dropdownItems={values(exportContent)}
          dropdownSubItems={values(ANNOTATION_EXPORT_FORMATS)}
          onSelect={downloadAnnotations}
          downloadVideo={downloadVideo}
        />
      )}
    </>
  );
};
VideoFeatureButtons.defaultProps = {
  downloadVideo: () => {}
};
VideoFeatureButtons.propTypes = {
  buttonStates: PropTypes.object.isRequired,
  isClickableDivHidden: PropTypes.bool.isRequired,
  isPublic: PropTypes.bool.isRequired,
  isFree: PropTypes.bool.isRequired,
  showIAModal: PropTypes.bool.isRequired,
  downloadAnnotations: PropTypes.func.isRequired,
  toggleButtonSwitch: PropTypes.func.isRequired,
  changeVideoContainerStateProperty: PropTypes.func.isRequired,
  video: PropTypes.object.isRequired,
  downloadVideo: PropTypes.func
};

export default VideoFeatureButtons;
